/**
 * City Agency Pages — Wave 8
 * Kyiv, Tashkent, Baku, Yerevan, Skopje, Sofia, Belgrade, Montevideo, San José (CR), Port Louis
 */
import type { CityAgencyData } from './city-agency-pages';

const sharedServices = [
  { name: 'Custom SaaS Development', description: 'End-to-end SaaS product development: architecture, development, deployment, and scaling.' },
  { name: 'Web Application Development', description: 'Enterprise web applications, portals, dashboards, and internal tools with modern stack.' },
  { name: 'Mobile App Development', description: 'Native iOS/Android and cross-platform apps with React Native and Flutter.' },
  { name: 'AI & Machine Learning', description: 'AI-powered solutions: NLP, computer vision, predictive analytics, and LLM integration.' },
  { name: 'Cloud & DevOps', description: 'AWS, Azure, GCP infrastructure. CI/CD pipelines, containerization, and managed services.' },
  { name: 'Staff Augmentation', description: 'Dedicated developers, QA engineers, DevOps specialists integrated into your team.' },
  { name: 'UI/UX Design', description: 'User research, wireframing, prototyping, and design systems with accessibility compliance.' },
  { name: 'Cybersecurity', description: 'Penetration testing, security audits, SOC operations, and compliance consulting.' },
];

const sharedIndustries = [
  { name: 'FinTech & Banking', description: 'Payment platforms, trading systems, compliance tools with PCI-DSS and SOC 2.' },
  { name: 'E-commerce & Retail', description: 'Online stores, marketplace platforms, inventory management solutions.' },
  { name: 'Healthcare & MedTech', description: 'Patient portals, telemedicine, EHR integrations, HIPAA-compliant applications.' },
  { name: 'SaaS & Technology', description: 'Product development and modernization for SaaS companies at any stage.' },
  { name: 'Real Estate & PropTech', description: 'Property management platforms, virtual tours, and real estate marketplaces.' },
  { name: 'Logistics & Supply Chain', description: 'Fleet management, route optimization, warehouse and supply chain platforms.' },
];

export const CITY_AGENCY_PAGES_WAVE8: Record<string, CityAgencyData> = {

  kyiv: {
    slug: 'software-development-agency-kyiv',
    cityName: 'Kyiv',
    countryName: 'Ukraine',
    flag: '🇺🇦',
    timezone: 'EET (GMT+2)',
    currency: 'UAH',
    title: {
      en: 'Software Development Agency Kyiv | Call IT Dev',
      fr: 'Agence Développement Informatique Kyiv | Call IT Dev',
      de: 'Softwareentwicklung Agentur Kiew | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Kyiv, Ukraine. Custom SaaS, web, mobile, AI. From €20/hr. 60% savings vs Western European rates.',
      fr: 'Agence développement informatique pour Kyiv. SaaS, web, mobile, IA. Dès 20 €/h. 60% d\'économies.',
      de: 'Softwareentwicklungsagentur für Kiew. SaaS, Web, Mobile, KI. Ab 20 €/h. 60% Ersparnis.',
    },
    keywords: ['software development agency Kyiv', 'IT outsourcing Ukraine', 'nearshore development Kyiv', 'web development Ukraine', 'Ukrainian developers'],
    heroTitle: {
      en: 'Software Development Agency for Kyiv',
      fr: 'Agence de Développement Informatique pour Kyiv',
      de: 'Softwareentwicklung Agentur für Kiew',
    },
    heroSubtitle: {
      en: '1-hour time difference from CET, 60% savings. Access world-class Ukrainian engineering talent through our nearshore model.',
      fr: '1h de décalage vs CET, 60% d\'économies. Accédez aux talents ukrainiens via notre modèle nearshore.',
      de: '1h Zeitunterschied zu CET, 60% Ersparnis. Zugang zu ukrainischen Ingenieurtalenten.',
    },
    whyTitle: {
      en: 'Why Kyiv Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Kyiv Choisissent Call IT Dev',
      de: 'Warum Kiewer Unternehmen Call IT Dev Wählen',
    },
    whyReasons: [
      { icon: '⏰', title: '1h Time Difference', description: 'Near-identical working hours with Ukraine for real-time collaboration.' },
      { icon: '💰', title: '60% Cost Savings', description: 'Senior devs at €20-35/hr vs €50-80/hr in Kyiv agencies.' },
      { icon: '🎓', title: 'Top Engineering Talent', description: '200+ engineers with deep expertise in modern tech stacks.' },
      { icon: '🌍', title: 'Multilingual Teams', description: 'English, French, and Russian-speaking engineers available.' },
      { icon: '🛡️', title: 'GDPR Compliant', description: 'Enterprise security standards, full EU GDPR compliance.' },
      { icon: '🚀', title: '2-Week Team Setup', description: 'Dedicated team operational in just 2 weeks.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why nearshore vs Kyiv agencies?', answer: 'Same quality, 1h timezone difference, 60% savings. Our teams integrate seamlessly into Ukrainian company workflows.' },
      { question: 'Technologies?', answer: 'React, TypeScript, Node.js, Python, Java, .NET, AWS, Azure, GCP, Docker, Kubernetes.' },
      { question: 'GDPR compliance?', answer: 'Enterprise security standards, GDPR compliant with signed DPAs.' },
      { question: 'How fast to start?', answer: 'CVs in 5 days, team onboarding in 2 weeks.' },
      { question: 'Engagement models?', answer: 'T&M, fixed-price, dedicated teams. Monthly contracts, no lock-in.' },
      { question: 'IP ownership?', answer: 'All IP belongs to you. NDAs and IP assignment from day one.' },
    ],
    localRate: '€50-80',
    ourRate: '€20-35',
    savings: '60%+',
  },

  tashkent: {
    slug: 'software-development-agency-tashkent',
    cityName: 'Tashkent',
    countryName: 'Uzbekistan',
    flag: '🇺🇿',
    timezone: 'UZT (GMT+5)',
    currency: 'UZS',
    title: {
      en: 'Software Development Agency Tashkent | Call IT Dev',
      fr: 'Agence Développement Informatique Tachkent | Call IT Dev',
      ru: 'Агентство Разработки ПО Ташкент | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Tashkent, Uzbekistan. Custom SaaS, web, mobile, AI. From $18/hr. 70% savings.',
      fr: 'Agence développement informatique pour Tachkent. SaaS, web, mobile, IA. Dès 18 $/h.',
      ru: 'Агентство разработки ПО для Ташкента. SaaS, web, mobile, AI. От 18 $/ч. Экономия 70%.',
    },
    keywords: ['software development agency Tashkent', 'IT outsourcing Uzbekistan', 'nearshore development Central Asia', 'web development Tashkent'],
    heroTitle: {
      en: 'Software Development Agency for Tashkent',
      fr: 'Agence de Développement Informatique pour Tachkent',
      ru: 'Агентство Разработки ПО для Ташкента',
    },
    heroSubtitle: {
      en: 'Russian & English-speaking engineers, 70% savings. Your Central Asian nearshore development partner.',
      fr: 'Ingénieurs russophones et anglophones, 70% d\'économies. Partenaire nearshore en Asie Centrale.',
      ru: 'Русскоговорящие инженеры, экономия 70%. Ваш nearshore-партнёр в Центральной Азии.',
    },
    whyTitle: {
      en: 'Why Tashkent Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Tachkent Choisissent Call IT Dev',
      ru: 'Почему Компании Ташкента Выбирают Call IT Dev',
    },
    whyReasons: [
      { icon: '🗣️', title: 'Russian & English', description: 'Bilingual teams for CIS and international markets.' },
      { icon: '💰', title: '70% Savings', description: 'Enterprise development at highly competitive rates.' },
      { icon: '🎓', title: 'Expert Engineers', description: '200+ full-stack, AI, and DevOps specialists.' },
      { icon: '🛡️', title: 'Security First', description: 'ISO 27001, GDPR compliance, enterprise security.' },
      { icon: '🌍', title: 'CIS Expertise', description: 'Deep understanding of Central Asian business culture.' },
      { icon: '🚀', title: 'Fast Delivery', description: 'Team operational in 2 weeks.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Do you have Russian-speaking teams?', answer: 'Yes. Native Russian speakers available for CIS markets.' },
      { question: 'Technologies?', answer: 'React, Node.js, Python, Java, AWS, Azure, GCP, mobile.' },
      { question: 'Data security?', answer: 'Enterprise security standards, GDPR compliant.' },
      { question: 'How to start?', answer: 'Free consultation, team ready in 2 weeks.' },
      { question: 'Minimum team?', answer: '2 developers, scale to 50+.' },
      { question: 'Contracts?', answer: 'Monthly flexibility, no long-term lock-in.' },
    ],
    localRate: '$30-50',
    ourRate: '$18-30',
    savings: '70%+',
  },

  baku: {
    slug: 'software-development-agency-baku',
    cityName: 'Baku',
    countryName: 'Azerbaijan',
    flag: '🇦🇿',
    timezone: 'AZT (GMT+4)',
    currency: 'AZN',
    title: {
      en: 'Software Development Agency Baku | Call IT Dev',
      fr: 'Agence Développement Informatique Bakou | Call IT Dev',
      ru: 'Агентство Разработки ПО Баку | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Baku, Azerbaijan. Custom SaaS, web, mobile, AI. From $20/hr. 65% savings.',
      fr: 'Agence développement informatique pour Bakou. SaaS, web, mobile, IA. Dès 20 $/h.',
      ru: 'Агентство разработки ПО для Баку. SaaS, web, mobile, AI. От 20 $/ч.',
    },
    keywords: ['software development agency Baku', 'IT outsourcing Azerbaijan', 'nearshore development Baku', 'web development Azerbaijan'],
    heroTitle: {
      en: 'Software Development Agency for Baku',
      fr: 'Agence de Développement Informatique pour Bakou',
      ru: 'Агентство Разработки ПО для Баку',
    },
    heroSubtitle: {
      en: 'Multilingual engineers, 65% savings vs local rates. Nearshore development for Caucasus businesses.',
      fr: 'Ingénieurs multilingues, 65% d\'économies. Développement nearshore pour le Caucase.',
      ru: 'Мультиязычные инженеры, экономия 65%. Nearshore-разработка для Кавказа.',
    },
    whyTitle: {
      en: 'Why Baku Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Bakou Choisissent Call IT Dev',
      ru: 'Почему Компании Баку Выбирают Call IT Dev',
    },
    whyReasons: [
      { icon: '🌍', title: 'Multilingual', description: 'English, Russian, Turkish-speaking engineering teams.' },
      { icon: '💰', title: '65% Savings', description: 'Enterprise-grade development at nearshore rates.' },
      { icon: '🎓', title: 'Expert Engineers', description: '200+ specialists across modern stacks.' },
      { icon: '⏰', title: '3h Time Difference', description: 'Significant working hours overlap with Baku.' },
      { icon: '🛡️', title: 'Enterprise Security', description: 'ISO 27001, GDPR compliance.' },
      { icon: '🚀', title: 'Quick Setup', description: 'Team ready in 2 weeks.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Languages supported?', answer: 'English, Russian, Turkish, French, Arabic speaking teams available.' },
      { question: 'Technologies?', answer: 'React, TypeScript, Node.js, Python, Java, AWS, Azure, GCP.' },
      { question: 'Security?', answer: 'ISO 27001, GDPR, enterprise security standards.' },
      { question: 'How to start?', answer: 'Free consultation, CVs in 5 days, team in 2 weeks.' },
      { question: 'Minimum team?', answer: '2 developers, scale to 50+.' },
      { question: 'IP ownership?', answer: 'All IP belongs to you from day one.' },
    ],
    localRate: '$40-65',
    ourRate: '$20-35',
    savings: '65%+',
  },

  yerevan: {
    slug: 'software-development-agency-yerevan',
    cityName: 'Yerevan',
    countryName: 'Armenia',
    flag: '🇦🇲',
    timezone: 'AMT (GMT+4)',
    currency: 'AMD',
    title: {
      en: 'Software Development Agency Yerevan | Call IT Dev',
      fr: 'Agence Développement Informatique Erevan | Call IT Dev',
      ru: 'Агентство Разработки ПО Ереван | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Yerevan, Armenia. Custom SaaS, web, mobile, AI. From $20/hr. 65% savings.',
      fr: 'Agence développement informatique pour Erevan. SaaS, web, mobile, IA. Dès 20 $/h.',
      ru: 'Агентство разработки ПО для Еревана. SaaS, web, mobile, AI. От 20 $/ч.',
    },
    keywords: ['software development agency Yerevan', 'IT outsourcing Armenia', 'nearshore development Yerevan', 'web development Armenia'],
    heroTitle: {
      en: 'Software Development Agency for Yerevan',
      fr: 'Agence de Développement Informatique pour Erevan',
      ru: 'Агентство Разработки ПО для Еревана',
    },
    heroSubtitle: {
      en: 'Strong engineering talent, 65% savings. Armenia\'s thriving tech ecosystem meets nearshore excellence.',
      fr: 'Talents d\'ingénierie, 65% d\'économies. L\'écosystème tech arménien rencontre l\'excellence nearshore.',
      ru: 'Сильные инженерные кадры, экономия 65%. Армянская IT-экосистема и nearshore-качество.',
    },
    whyTitle: {
      en: 'Why Yerevan Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises d\'Erevan Choisissent Call IT Dev',
      ru: 'Почему Компании Еревана Выбирают Call IT Dev',
    },
    whyReasons: [
      { icon: '🎓', title: 'Tech Hub', description: 'Yerevan is a growing tech hub — our teams complement local ecosystems.' },
      { icon: '💰', title: '65% Savings', description: 'Enterprise development at competitive nearshore rates.' },
      { icon: '🗣️', title: 'Multilingual', description: 'English, Russian, French-speaking engineering teams.' },
      { icon: '⏰', title: '3h Overlap', description: 'Significant working hours overlap for collaboration.' },
      { icon: '🛡️', title: 'Security First', description: 'ISO 27001, GDPR, enterprise-grade security.' },
      { icon: '🚀', title: 'Fast Delivery', description: 'Team operational in 2 weeks.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why nearshore for Yerevan?', answer: 'Access 200+ engineers at 65% savings. Enterprise quality, flexible engagement.' },
      { question: 'Technologies?', answer: 'React, Node.js, Python, Java, AWS, Azure, GCP.' },
      { question: 'Data security?', answer: 'ISO 27001, GDPR compliant, signed NDAs.' },
      { question: 'Communication?', answer: 'English and Russian fluent teams, daily standups.' },
      { question: 'How to start?', answer: 'Free consultation, team in 2 weeks.' },
      { question: 'Scaling?', answer: 'Start with 2, scale to 50+.' },
    ],
    localRate: '$40-60',
    ourRate: '$20-35',
    savings: '65%+',
  },

  skopje: {
    slug: 'software-development-agency-skopje',
    cityName: 'Skopje',
    countryName: 'North Macedonia',
    flag: '🇲🇰',
    timezone: 'CET (GMT+1)',
    currency: 'MKD',
    title: {
      en: 'Software Development Agency Skopje | Call IT Dev',
      fr: 'Agence Développement Informatique Skopje | Call IT Dev',
      de: 'Softwareentwicklung Agentur Skopje | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Skopje, North Macedonia. Custom SaaS, web, mobile, AI. CET timezone, from €18/hr. 60% savings.',
      fr: 'Agence développement informatique pour Skopje. SaaS, web, mobile, IA. Fuseau CET, dès 18 €/h.',
      de: 'Softwareentwicklungsagentur für Skopje. SaaS, Web, Mobile, KI. CET-Zeitzone, ab 18 €/h.',
    },
    keywords: ['software development agency Skopje', 'IT outsourcing North Macedonia', 'nearshore development Skopje', 'web development Macedonia'],
    heroTitle: {
      en: 'Software Development Agency for Skopje',
      fr: 'Agence de Développement Informatique pour Skopje',
      de: 'Softwareentwicklung Agentur für Skopje',
    },
    heroSubtitle: {
      en: 'Same CET timezone, 60% savings vs Western rates. EU-aspirant quality standards at nearshore prices.',
      fr: 'Même fuseau CET, 60% d\'économies. Standards de qualité EU-candidat à prix nearshore.',
      de: 'Gleiche CET-Zeitzone, 60% Ersparnis. EU-Kandidat-Qualitätsstandards zu Nearshore-Preisen.',
    },
    whyTitle: {
      en: 'Why Skopje Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Skopje Choisissent Call IT Dev',
      de: 'Warum Unternehmen in Skopje Call IT Dev Wählen',
    },
    whyReasons: [
      { icon: '⏰', title: 'Same CET Timezone', description: 'Real-time collaboration with identical working hours.' },
      { icon: '💰', title: '60% Savings', description: 'Senior devs at €18-30/hr vs €40-65/hr locally.' },
      { icon: '🎓', title: 'Engineering Talent', description: '200+ engineers across modern stacks.' },
      { icon: '🌍', title: 'Multilingual', description: 'English, French, German-speaking teams.' },
      { icon: '🛡️', title: 'GDPR Aligned', description: 'ISO 27001, GDPR-aligned processes.' },
      { icon: '🚀', title: '2-Week Setup', description: 'Dedicated team operational fast.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why nearshore for Skopje?', answer: 'Same CET timezone, 60% lower rates, enterprise quality.' },
      { question: 'Technologies?', answer: 'React, TypeScript, Node.js, Python, Java, cloud-native.' },
      { question: 'Data protection?', answer: 'ISO 27001, GDPR-aligned, enterprise security.' },
      { question: 'How to start?', answer: 'CVs in 5 days, team in 2 weeks.' },
      { question: 'Flexibility?', answer: 'Monthly contracts, no lock-in.' },
      { question: 'Scaling?', answer: 'Start with 2, scale to 50+.' },
    ],
    localRate: '€40-65',
    ourRate: '€18-30',
    savings: '60%+',
  },

  sofia: {
    slug: 'software-development-agency-sofia',
    cityName: 'Sofia',
    countryName: 'Bulgaria',
    flag: '🇧🇬',
    timezone: 'EET (GMT+2)',
    currency: 'BGN/EUR',
    title: {
      en: 'Software Development Agency Sofia | Call IT Dev',
      fr: 'Agence Développement Informatique Sofia | Call IT Dev',
      de: 'Softwareentwicklung Agentur Sofia | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Sofia, Bulgaria. Custom SaaS, web, mobile, AI. From €20/hr. 55% savings vs local rates.',
      fr: 'Agence développement informatique pour Sofia. SaaS, web, mobile, IA. Dès 20 €/h. 55% d\'économies.',
      de: 'Softwareentwicklungsagentur für Sofia. SaaS, Web, Mobile, KI. Ab 20 €/h. 55% Ersparnis.',
    },
    keywords: ['software development agency Sofia', 'IT outsourcing Bulgaria', 'nearshore development Sofia', 'web development Bulgaria'],
    heroTitle: {
      en: 'Software Development Agency for Sofia',
      fr: 'Agence de Développement Informatique pour Sofia',
      de: 'Softwareentwicklung Agentur für Sofia',
    },
    heroSubtitle: {
      en: '1-hour time difference, 55% savings vs Sofia agencies. EU-member quality at nearshore prices.',
      fr: '1h de décalage, 55% d\'économies vs agences de Sofia. Qualité UE à prix nearshore.',
      de: '1h Zeitunterschied, 55% Ersparnis vs Sofioter Agenturen. EU-Qualität zu Nearshore-Preisen.',
    },
    whyTitle: {
      en: 'Why Sofia Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Sofia Choisissent Call IT Dev',
      de: 'Warum Sofioter Unternehmen Call IT Dev Wählen',
    },
    whyReasons: [
      { icon: '⏰', title: '1h Time Difference', description: 'Near-identical working hours with Bulgaria.' },
      { icon: '💰', title: '55% Savings', description: 'Senior devs at €20-35/hr vs €45-70/hr in Sofia.' },
      { icon: '🇪🇺', title: 'EU Standards', description: 'ISO 27001, GDPR, EU data protection.' },
      { icon: '🎓', title: '200+ Engineers', description: 'Full-stack, AI, DevOps, mobile specialists.' },
      { icon: '🌍', title: 'English Fluent', description: 'C1/C2 English-speaking teams.' },
      { icon: '🚀', title: 'Fast Onboarding', description: 'Team operational in 2 weeks.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why nearshore vs Sofia agencies?', answer: '55% savings, same quality, 1h timezone difference.' },
      { question: 'Technologies?', answer: 'React, TypeScript, Node.js, Python, Java, AWS, Azure, GCP.' },
      { question: 'EU compliance?', answer: 'ISO 27001, GDPR compliant. Signed DPAs.' },
      { question: 'Minimum team?', answer: '2 developers, scale to 50+.' },
      { question: 'Communication?', answer: 'Daily standups, Slack/Teams, Jira, bi-weekly demos.' },
      { question: 'IP?', answer: 'All IP belongs to you. NDAs from day one.' },
    ],
    localRate: '€45-70',
    ourRate: '€20-35',
    savings: '55%+',
  },

  belgrade: {
    slug: 'software-development-agency-belgrade',
    cityName: 'Belgrade',
    countryName: 'Serbia',
    flag: '🇷🇸',
    timezone: 'CET (GMT+1)',
    currency: 'RSD/EUR',
    title: {
      en: 'Software Development Agency Belgrade | Call IT Dev',
      fr: 'Agence Développement Informatique Belgrade | Call IT Dev',
      de: 'Softwareentwicklung Agentur Belgrad | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Belgrade, Serbia. Custom SaaS, web, mobile, AI. CET timezone, from €18/hr. 60% savings.',
      fr: 'Agence développement informatique pour Belgrade. SaaS, web, mobile, IA. Fuseau CET, dès 18 €/h.',
      de: 'Softwareentwicklungsagentur für Belgrad. SaaS, Web, Mobile, KI. CET-Zeitzone, ab 18 €/h.',
    },
    keywords: ['software development agency Belgrade', 'IT outsourcing Serbia', 'nearshore development Belgrade', 'web development Serbia'],
    heroTitle: {
      en: 'Software Development Agency for Belgrade',
      fr: 'Agence de Développement Informatique pour Belgrade',
      de: 'Softwareentwicklung Agentur für Belgrad',
    },
    heroSubtitle: {
      en: 'Same CET timezone, 60% savings vs Belgrade agencies. Serbia\'s growing tech talent meets nearshore excellence.',
      fr: 'Même fuseau CET, 60% d\'économies. Les talents tech serbes rencontrent l\'excellence nearshore.',
      de: 'Gleiche CET-Zeitzone, 60% Ersparnis. Serbiens Tech-Talente treffen Nearshore-Exzellenz.',
    },
    whyTitle: {
      en: 'Why Belgrade Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Belgrade Choisissent Call IT Dev',
      de: 'Warum Belgrader Unternehmen Call IT Dev Wählen',
    },
    whyReasons: [
      { icon: '⏰', title: 'Same CET Timezone', description: 'Identical working hours for seamless collaboration.' },
      { icon: '💰', title: '60% Savings', description: 'Senior devs at €18-30/hr vs €40-65/hr locally.' },
      { icon: '🎓', title: 'Engineering Excellence', description: '200+ engineers across modern stacks.' },
      { icon: '🌍', title: 'Multilingual', description: 'English, French, German-speaking teams.' },
      { icon: '🛡️', title: 'GDPR Aligned', description: 'ISO 27001, GDPR-aligned processes.' },
      { icon: '🚀', title: 'Quick Start', description: '2-week onboarding, immediate productivity.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why nearshore for Belgrade?', answer: 'Same CET timezone, 60% savings, enterprise quality.' },
      { question: 'Technologies?', answer: 'React, TypeScript, Node.js, Python, Java, cloud-native.' },
      { question: 'Security?', answer: 'ISO 27001, GDPR-aligned, enterprise security.' },
      { question: 'How to start?', answer: 'CVs in 5 days, team in 2 weeks.' },
      { question: 'Contracts?', answer: 'Monthly flexibility, no lock-in.' },
      { question: 'Scaling?', answer: 'Start with 2, scale to 50+.' },
    ],
    localRate: '€40-65',
    ourRate: '€18-30',
    savings: '60%+',
  },

  montevideo: {
    slug: 'software-development-agency-montevideo',
    cityName: 'Montevideo',
    countryName: 'Uruguay',
    flag: '🇺🇾',
    timezone: 'UYT (GMT-3)',
    currency: 'UYU',
    title: {
      en: 'Software Development Agency Montevideo | Call IT Dev',
      fr: 'Agence Développement Informatique Montevideo | Call IT Dev',
      es: 'Agencia Desarrollo Software Montevideo | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Montevideo, Uruguay. Custom SaaS, web, mobile, AI. From $22/hr. 60% savings vs US rates.',
      fr: 'Agence développement informatique pour Montevideo. SaaS, web, mobile, IA. Dès 22 $/h.',
      es: 'Agencia desarrollo de software para Montevideo. SaaS, web, móvil, IA. Desde 22 $/h. 60% ahorro.',
    },
    keywords: ['software development agency Montevideo', 'IT outsourcing Uruguay', 'nearshore development Montevideo', 'desarrollo software Uruguay'],
    heroTitle: {
      en: 'Software Development Agency for Montevideo',
      fr: 'Agence de Développement Informatique pour Montevideo',
      es: 'Agencia de Desarrollo de Software para Montevideo',
    },
    heroSubtitle: {
      en: 'Spanish & English speaking engineers, 60% savings vs US rates. Uruguay\'s stable economy meets nearshore innovation.',
      fr: 'Ingénieurs hispanophones et anglophones, 60% d\'économies vs tarifs US.',
      es: 'Ingenieros hispanohablantes y angloparlantes, 60% ahorro vs tarifas EEUU.',
    },
    whyTitle: {
      en: 'Why Montevideo Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Montevideo Choisissent Call IT Dev',
      es: 'Por Qué las Empresas de Montevideo Eligen Call IT Dev',
    },
    whyReasons: [
      { icon: '🗣️', title: 'Spanish & English', description: 'Bilingual teams for LATAM and North American markets.' },
      { icon: '💰', title: '60% Savings', description: 'Enterprise development at competitive nearshore rates.' },
      { icon: '🎓', title: 'Expert Engineers', description: '200+ specialists across modern stacks.' },
      { icon: '🌍', title: 'LATAM Expertise', description: 'Deep understanding of Latin American markets.' },
      { icon: '🛡️', title: 'Data Security', description: 'ISO 27001, GDPR compliance.' },
      { icon: '🚀', title: 'Fast Setup', description: 'Team ready in 2 weeks.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: '¿Tienen equipos hispanohablantes?', answer: 'Sí. Equipos nativos en español para mercados LATAM.' },
      { question: 'Technologies?', answer: 'React, Node.js, Python, Java, AWS, Azure, GCP, mobile.' },
      { question: 'Data protection?', answer: 'ISO 27001, GDPR compliant.' },
      { question: 'How to start?', answer: 'Free consultation, team in 2 weeks.' },
      { question: 'Minimum team?', answer: '2 developers, scale to 50+.' },
      { question: 'IP ownership?', answer: 'All IP belongs to you.' },
    ],
    localRate: '$45-70',
    ourRate: '$22-35',
    savings: '60%+',
  },

  sanJose: {
    slug: 'software-development-agency-san-jose-cr',
    cityName: 'San José',
    countryName: 'Costa Rica',
    flag: '🇨🇷',
    timezone: 'CST (GMT-6)',
    currency: 'CRC/USD',
    title: {
      en: 'Software Development Agency San José | Call IT Dev',
      fr: 'Agence Développement Informatique San José | Call IT Dev',
      es: 'Agencia Desarrollo Software San José Costa Rica | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving San José, Costa Rica. Custom SaaS, web, mobile, AI. From $22/hr. 60% savings vs US rates.',
      fr: 'Agence développement informatique pour San José, Costa Rica. SaaS, web, mobile, IA. Dès 22 $/h.',
      es: 'Agencia desarrollo de software para San José, Costa Rica. SaaS, web, móvil, IA. Desde 22 $/h.',
    },
    keywords: ['software development agency San Jose Costa Rica', 'IT outsourcing Costa Rica', 'nearshore development San Jose', 'desarrollo software Costa Rica'],
    heroTitle: {
      en: 'Software Development Agency for San José, Costa Rica',
      fr: 'Agence de Développement Informatique pour San José',
      es: 'Agencia de Desarrollo de Software para San José',
    },
    heroSubtitle: {
      en: 'Bilingual Spanish/English engineers, US-friendly timezone, 60% savings. Costa Rica\'s tech corridor at nearshore prices.',
      fr: 'Ingénieurs bilingues espagnol/anglais, fuseau US, 60% d\'économies.',
      es: 'Ingenieros bilingües español/inglés, zona horaria US, 60% ahorro.',
    },
    whyTitle: {
      en: 'Why San José Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de San José Choisissent Call IT Dev',
      es: 'Por Qué las Empresas de San José Eligen Call IT Dev',
    },
    whyReasons: [
      { icon: '🗣️', title: 'Bilingual Teams', description: 'Spanish/English engineers for US and LATAM.' },
      { icon: '💰', title: '60% Savings', description: 'Enterprise development at nearshore rates.' },
      { icon: '🌍', title: 'US-Friendly', description: 'Close timezone and cultural alignment with US.' },
      { icon: '🎓', title: 'Expert Engineers', description: '200+ specialists across modern stacks.' },
      { icon: '🛡️', title: 'Security', description: 'ISO 27001, GDPR compliance.' },
      { icon: '🚀', title: 'Quick Start', description: 'Team operational in 2 weeks.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: '¿Equipos bilingües?', answer: 'Sí. Todos nuestros ingenieros hablan español e inglés.' },
      { question: 'Technologies?', answer: 'React, TypeScript, Node.js, Python, Java, AWS, Azure, GCP.' },
      { question: 'Security?', answer: 'ISO 27001, GDPR compliant.' },
      { question: 'How to start?', answer: 'Free consultation, team in 2 weeks.' },
      { question: 'Contracts?', answer: 'Monthly flexibility, no long-term lock-in.' },
      { question: 'Scaling?', answer: 'Start with 2, scale to 50+.' },
    ],
    localRate: '$45-70',
    ourRate: '$22-35',
    savings: '60%+',
  },

  portLouis: {
    slug: 'software-development-agency-port-louis',
    cityName: 'Port Louis',
    countryName: 'Mauritius',
    flag: '🇲🇺',
    timezone: 'MUT (GMT+4)',
    currency: 'MUR',
    title: {
      en: 'Software Development Agency Port Louis | Call IT Dev',
      fr: 'Agence Développement Informatique Port Louis | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Port Louis, Mauritius. Custom SaaS, web, mobile, AI. From $20/hr. 65% savings. Bilingual FR/EN.',
      fr: 'Agence développement informatique pour Port Louis, Maurice. SaaS, web, mobile, IA. Dès 20 $/h. Bilingue FR/EN.',
    },
    keywords: ['software development agency Port Louis', 'IT outsourcing Mauritius', 'nearshore development Mauritius', 'développement logiciel Maurice'],
    heroTitle: {
      en: 'Software Development Agency for Port Louis',
      fr: 'Agence de Développement Informatique pour Port Louis',
    },
    heroSubtitle: {
      en: 'French & English speaking engineers, 65% savings. Mauritius\'s offshore hub meets nearshore development.',
      fr: 'Ingénieurs francophones et anglophones, 65% d\'économies. Le hub offshore mauricien rencontre le nearshore.',
    },
    whyTitle: {
      en: 'Why Port Louis Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Port Louis Choisissent Call IT Dev',
    },
    whyReasons: [
      { icon: '🗣️', title: 'French & English', description: 'Bilingual teams for Francophone and Anglophone markets.' },
      { icon: '💰', title: '65% Savings', description: 'Enterprise development at competitive rates.' },
      { icon: '🎓', title: 'Expert Engineers', description: '200+ engineers across modern stacks.' },
      { icon: '⏰', title: '3h Time Difference', description: 'Significant working hours overlap.' },
      { icon: '🛡️', title: 'Data Protection', description: 'ISO 27001, GDPR compliance, Mauritius DPA.' },
      { icon: '🚀', title: 'Fast Setup', description: 'Team ready in 2 weeks.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Avez-vous des équipes francophones?', answer: 'Oui. Tous nos ingénieurs parlent français et anglais.' },
      { question: 'Technologies?', answer: 'React, Node.js, Python, Java, AWS, Azure, GCP, mobile.' },
      { question: 'Data protection?', answer: 'ISO 27001, GDPR, Mauritius DPA compliant.' },
      { question: 'How to start?', answer: 'Free consultation, team in 2 weeks.' },
      { question: 'Minimum team?', answer: '2 developers, scale to 50+.' },
      { question: 'IP ownership?', answer: 'All IP belongs to you from day one.' },
    ],
    localRate: '$40-60',
    ourRate: '$20-35',
    savings: '65%+',
  },
};
